import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule } from '@porsche-design-system/components-angular';
import {
  distinctUntilChanged,
  EMPTY,
  merge,
  type Observable,
  of,
  shareReplay,
  skip,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { objectsEqual } from '../../shared/helpers/objects-equal.helper';
import type { EduBudgetCorrection } from '../../shared/models/api/edu-budget-correction.model';
import type { User } from '../../shared/models/api/user.model';
import { OpaCurrencyPipe } from '../../shared/pipes/opa-currency.pipe';
import { EduBudgetCorrectionService } from '../../shared/services/api/edu-budget-correction.service';
import { MediaQueryService } from '../../shared/services/media-query.service';

@Component({
  selector: 'opa-edu-budget-correction-details',
  imports: [CommonModule, OpaCurrencyPipe, PorscheDesignSystemModule, RouterLink, LetDirective],
  templateUrl: './edu-budget-correction-details.component.html',
  styleUrl: './edu-budget-correction-details.component.scss',
})
export class EduBudgetCorrectionDetailsComponent {
  private readonly mediaQueryService = inject(MediaQueryService);
  private readonly route = inject(ActivatedRoute);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly eduBudgetCorrectionService = inject(EduBudgetCorrectionService);

  readonly mediaMaxXs$ = this.mediaQueryService.max('xs');
  eduBudgetCorrection$ = this.getEduBudgetCorrection$();

  reloadEduBudgetCorrection(markForCheck = false): void {
    this.eduBudgetCorrection$ = this.getEduBudgetCorrection$();
    if (markForCheck) this.changeDetectorRef.markForCheck();
  }

  trackUser(index: number, user: User): number {
    return user.id;
  }

  private getEduBudgetCorrection$(): Observable<EduBudgetCorrection> {
    const routeParams$ = merge(
      of(this.route.snapshot.params),
      this.route.params.pipe(
        startWith(this.route.snapshot.params),
        distinctUntilChanged((a, b) => objectsEqual(a, b)),
        skip(1),
        tap(() => this.reloadEduBudgetCorrection(true)),
        switchMap(() => EMPTY),
      ),
    );

    return routeParams$.pipe(
      switchMap((routeParams) => {
        const eduBudgetCorrectionId = Number(routeParams['id']);
        return this.eduBudgetCorrectionService.getEduBudgetCorrection(eduBudgetCorrectionId);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}
